import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Box,
  Chip,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  LinearProgress,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  Groups as GroupsIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useLanguage } from '../contexts/LanguageContext';

interface Tanda {
  id: number;
  name: string;
  description: string;
  total_amount: number;
  member_count: number;
  contribution_amount: number;
  frequency: string;
  status: string;
  start_date: string;
  current_members: number;
  organizer: {
    id: number;
    username: string;
    full_name: string;
  };
}

const Tandas: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [tandas, setTandas] = useState<Tanda[]>([]);
  const [myTandaIds, setMyTandaIds] = useState<Set<number>>(new Set());
  const [filteredTandas, setFilteredTandas] = useState<Tanda[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [frequencyFilter, setFrequencyFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('newest');

  useEffect(() => {
    fetchTandas();
  }, []);

  useEffect(() => {
    filterAndSortTandas();
  }, [tandas, searchTerm, statusFilter, frequencyFilter, sortBy, tabValue]);

  const fetchTandas = async () => {
    try {
      setLoading(true);
      const [allResponse, myResponse] = await Promise.all([
        axios.get('/tandas'),
        axios.get('/tandas/my'),
      ]);
      setTandas(allResponse.data);
      setMyTandaIds(new Set(myResponse.data.map((t: Tanda) => t.id)));
    } catch (error) {
      console.error('Failed to fetch tandas:', error);
      enqueueSnackbar('Failed to load tandas', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortTandas = () => {
    let filtered = [...tandas];

    // Filter by tab (my tandas vs all tandas)
    if (tabValue === 1) {
      filtered = filtered.filter(tanda => myTandaIds.has(tanda.id));
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(tanda =>
        tanda.name.toLowerCase().includes(term) ||
        tanda.description.toLowerCase().includes(term) ||
        tanda.organizer.full_name.toLowerCase().includes(term)
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(tanda => tanda.status === statusFilter);
    }

    // Filter by frequency
    if (frequencyFilter !== 'all') {
      filtered = filtered.filter(tanda => tanda.frequency === frequencyFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.start_date).getTime() - new Date(a.start_date).getTime();
        case 'oldest':
          return new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
        case 'amount_high':
          return b.total_amount - a.total_amount;
        case 'amount_low':
          return a.total_amount - b.total_amount;
        case 'members_high':
          return b.current_members - a.current_members;
        case 'members_low':
          return a.current_members - b.current_members;
        default:
          return 0;
      }
    });

    setFilteredTandas(filtered);
  };

  const handleJoinTanda = async (tandaId: number) => {
    try {
      await axios.post(`/tandas/${tandaId}/members/me`);
      enqueueSnackbar('Successfully joined tanda!', { variant: 'success' });
      fetchTandas();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to join tanda', { variant: 'error' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'forming': return 'warning';
      case 'completed': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getFrequencyLabel = (frequency: string) => {
    switch (frequency) {
      case 'weekly': return t('common.weekly');
      case 'biweekly': return t('common.biweekly');
      case 'monthly': return t('common.monthly');
      default: return frequency;
    }
  };

  const isMember = (tanda: Tanda) => {
    return myTandaIds.has(tanda.id);
  };

  const canJoin = (tanda: Tanda) => {
    return !isMember(tanda) && tanda.status === 'forming' && tanda.current_members < tanda.member_count;
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('tandas.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('tandas.subtitle')}
        </Typography>
      </Box>

      {/* Search and Filter Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder={t('tandas.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={(e) => setFilterAnchorEl(e.currentTarget)}
              >
                {t('tandas.filter')}
              </Button>

              <Button
                variant="outlined"
                startIcon={<SortIcon />}
                onClick={(e) => setSortAnchorEl(e.currentTarget)}
              >
                {t('tandas.sort')}
              </Button>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/create-tanda')}
              >
                {t('tandas.createTanda')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={() => setFilterAnchorEl(null)}
      >
        <MenuItem>
          <FormControl fullWidth size="small">
            <InputLabel>{t('tandas.status')}</InputLabel>
            <Select
              value={statusFilter}
              label={t('tandas.status')}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">{t('tandas.allStatus')}</MenuItem>
              <MenuItem value="forming">{t('tandas.forming')}</MenuItem>
              <MenuItem value="active">{t('tandas.active')}</MenuItem>
              <MenuItem value="completed">{t('tandas.completed')}</MenuItem>
              <MenuItem value="cancelled">{t('tandas.cancelled')}</MenuItem>
            </Select>
          </FormControl>
        </MenuItem>
        <MenuItem>
          <FormControl fullWidth size="small">
            <InputLabel>{t('tandas.frequency')}</InputLabel>
            <Select
              value={frequencyFilter}
              label={t('tandas.frequency')}
              onChange={(e) => setFrequencyFilter(e.target.value)}
            >
              <MenuItem value="all">{t('tandas.allFrequencies')}</MenuItem>
              <MenuItem value="weekly">{t('tandas.weekly')}</MenuItem>
              <MenuItem value="biweekly">{t('tandas.biweekly')}</MenuItem>
              <MenuItem value="monthly">{t('tandas.monthly')}</MenuItem>
            </Select>
          </FormControl>
        </MenuItem>
      </Menu>

      {/* Sort Menu */}
      <Menu
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={() => setSortAnchorEl(null)}
      >
        <MenuItem onClick={() => { setSortBy('newest'); setSortAnchorEl(null); }}>
          {t('tandas.newest')}
        </MenuItem>
        <MenuItem onClick={() => { setSortBy('oldest'); setSortAnchorEl(null); }}>
          {t('tandas.oldest')}
        </MenuItem>
        <MenuItem onClick={() => { setSortBy('amount_high'); setSortAnchorEl(null); }}>
          {t('tandas.highestAmount')}
        </MenuItem>
        <MenuItem onClick={() => { setSortBy('amount_low'); setSortAnchorEl(null); }}>
          {t('tandas.lowestAmount')}
        </MenuItem>
        <MenuItem onClick={() => { setSortBy('members_high'); setSortAnchorEl(null); }}>
          {t('tandas.mostMembers')}
        </MenuItem>
        <MenuItem onClick={() => { setSortBy('members_low'); setSortAnchorEl(null); }}>
          {t('tandas.fewestMembers')}
        </MenuItem>
      </Menu>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          variant="fullWidth"
        >
          <Tab label={t('tandas.allTandas')} />
          <Tab label={t('tandas.myTandas')} />
        </Tabs>
      </Paper>

      {/* Results Count */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t('tandas.showing')} {filteredTandas.length} {t('tandas.of')} {tandas.length} tandas
        </Typography>
      </Box>

      {/* Tanda Cards */}
      {filteredTandas.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <GroupsIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            {t('tandas.noTandas')}
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {searchTerm || statusFilter !== 'all' || frequencyFilter !== 'all'
              ? t('tandas.adjustFilters')
              : tabValue === 1
                ? t('tandas.noJoined')
                : t('tandas.noAvailable')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/create-tanda')}
          >
            {t('tandas.createFirst')}
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredTandas.map((tanda) => (
            <Grid item xs={12} sm={6} md={4} key={tanda.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out',
                  },
                }}
                onClick={() => navigate(`/tandas/${tanda.id}`)}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                      {tanda.name}
                    </Typography>
                    <Chip
                      label={tanda.status.toUpperCase()}
                      size="small"
                      color={getStatusColor(tanda.status) as any}
                    />
                  </Box>

                  {/* Description */}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, height: 40, overflow: 'hidden' }}>
                    {tanda.description || 'No description provided.'}
                  </Typography>

                  {/* Stats */}
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <GroupsIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {tanda.current_members}/{tanda.member_count}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <MoneyIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          ${tanda.contribution_amount} MXN
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ScheduleIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {getFrequencyLabel(tanda.frequency)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" noWrap>
                          {tanda.organizer.full_name.split(' ')[0]}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Progress Bar */}
                  <Box sx={{ mb: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={(tanda.current_members / tanda.member_count) * 100}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                    <Typography variant="caption" color="text.secondary" align="center" display="block">
                      {Math.round((tanda.current_members / tanda.member_count) * 100)}% {t('tandas.filled')}
                    </Typography>
                  </Box>

                  {/* Total Amount */}
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography variant="h6" color="primary">
                      ${tanda.total_amount} MXN
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('tandas.totalPot')}
                    </Typography>
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/tandas/${tanda.id}`);
                      }}
                    >
                      {t('tandas.viewDetails')}
                    </Button>

                    {canJoin(tanda) && (
                      <Button
                        variant="contained"
                        fullWidth
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleJoinTanda(tanda.id);
                        }}
                      >
                        {t('tandas.join')}
                      </Button>
                    )}

                    {isMember(tanda) && (
                      <Chip
                        label={t('tandas.member')}
                        color="primary"
                        size="small"
                        sx={{ width: '100%' }}
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Info Card */}
      <Paper sx={{ p: 3, mt: 4, backgroundColor: 'info.light', color: 'info.contrastText' }}>
        <Typography variant="h6" gutterBottom>
          {t('tandas.whatIsTanda')}
        </Typography>
        <Typography variant="body2">
          {t('tandas.tandaExplanation')}
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>{t('tandas.benefits')}</strong>
            <br />
            {t('tandas.benefit1')}
            <br />
            {t('tandas.benefit2')}
            <br />
            {t('tandas.benefit3')}
            <br />
            {t('tandas.benefit4')}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default Tandas;
